import React, { FC, PropsWithChildren, useMemo, useState } from 'react';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { EmotionValue } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { Button, ButtonSize, ButtonVariant, ButtonTheme } from '@/shared/components/Button/Button';

import { useI18n } from '../../hooks/useI18n';

import styles from './EmotionSelector.module.css';
import { CrossIcon } from './icons/cross-icon';

export type Emotion = {
    value: EmotionValue;
    name: string;
    icon: string;
};

interface Props {
    buttonType: ButtonVariant;
    size: ButtonSize;
    colorScheme: ButtonTheme;
    activeValue: EmotionValue | undefined;
    emotions: Emotion[];
    onCancelClick: () => void;
    onClick: (e: Emotion) => void;
}

const APPEAR_DURATION = 500;
const FADE_OUT_DURATION = 350;

const buttonsAnimationClassNames = {
    appear: styles.buttonsAppear,
    appearActive: styles.buttonsAppearActive,
    exit: styles.buttonsExit,
    exitActive: styles.buttonsExitActive,
};

export const EmotionSelector: FC<PropsWithChildren<Props>> = ({
    buttonType,
    size,
    colorScheme,
    activeValue,
    emotions,
    onClick,
    onCancelClick,
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const i18n = useI18n();

    const handleCancelClick = () => {
        setIsOpen(false);
    };

    const handleTransitionExited = () => {
        onCancelClick();
    };

    const allButtons = useMemo(
        () => [
            {
                key: 'DISMISS',
                name: i18n('content_actions__emotions_dismiss'),
                icon: <CrossIcon width={24} height={24} fill="white" />,
                onClick: handleCancelClick,
                isActive: false,
            },
            ...emotions.map((item) => ({
                key: item.value,
                name: item.name,
                icon: <img src={item.icon} alt="" height={27} />,
                onClick: () => {
                    onClick(item);
                    handleCancelClick();
                },
                isActive: activeValue === item.value,
            })),
        ],
        [i18n, emotions, activeValue, onClick]
    );

    return (
        <ul className={styles.root}>
            {allButtons.map((button) => (
                <CSSTransition
                    key={button.key}
                    in={isOpen}
                    classNames={buttonsAnimationClassNames}
                    timeout={{
                        enter: APPEAR_DURATION,
                        exit: FADE_OUT_DURATION,
                    }}
                    onExited={handleTransitionExited}
                    appear
                >
                    <li
                        className={classNames(
                            styles.buttonWrapper,
                            button.isActive && styles.activeButton
                        )}
                    >
                        <Button
                            name={button.key}
                            variant={button.isActive ? 'accentWhite' : buttonType}
                            size={size}
                            theme={colorScheme}
                            startIcon={button.icon}
                            aria-label={button.name}
                            onClick={button.onClick}
                            aria-current={button.isActive}
                        />
                        <span className={styles.note}>{button.name}</span>
                    </li>
                </CSSTransition>
            ))}
        </ul>
    );
};
